import { useParams, useRouteMatch } from 'peach/router'
import { Breadcrumb, LoadingContainer, Panels, Bar } from 'peach/components'
import { getName } from 'peach/helpers'

import { Page, DynamicForm } from 'core/components'
import { useCreditAgencyGet, useCreditAgencyUpdate } from 'core/types'
import { addToast } from 'core/stores/toasts'

const CreditAgency = () => {
  const { url } = useRouteMatch()
  const { companyId, creditAgencyId } = useParams<{
    companyId?: string
    creditAgencyId?: string
  }>()

  const {
    data: creditAgency,
    isLoading,
    refetch,
  } = useCreditAgencyGet({
    companyId,
    creditAgencyId,
  })

  const { mutateAsync: updateCreditAgency } = useCreditAgencyUpdate({
    companyId,
    creditAgencyId,
  })

  const title = getName(creditAgency) || 'Credit Agency'

  return (
    <Panels header={<Bar title={title} />}>
      <LoadingContainer isLoading={isLoading}>
        <Breadcrumb title={title} to={url} />
        <Page variant='full'>
          <DynamicForm
            data={creditAgency || {}}
            fieldEditMode='toggle'
            fieldOverrides={{
              specificDays: 'JsonInput',
              agencyName: 'Select',
              sftpAccessMethod: 'Select',
              uploadFrequency: 'Select',
            }}
            omitFields={[
              'id',
              'createdAt',
              'updatedAt',
              'deletedAt',
              'object',
              'companyId',
              'isPeachAggregator',
            ]}
            onSubmit={async (payload: any) => {
              // Convert null/empty strings to appropriate values per API requirements
              const cleanedPayload: any = {
                ...payload,
                // isPeachAggregator: deprecated, must always be false
                isPeachAggregator: false,
                // fileNamePrefix: string (not nullable) - convert null to empty string, keep empty string as is
                fileNamePrefix:
                  payload.fileNamePrefix === null ||
                  payload.fileNamePrefix === undefined
                    ? ''
                    : payload.fileNamePrefix,
                // sftpCdDir: string (not nullable) - convert null to empty string
                sftpCdDir: payload.sftpCdDir === null ? '' : payload.sftpCdDir,
                // sftpHostname: string or null - convert empty string to null
                sftpHostname:
                  payload.sftpHostname === '' ? null : payload.sftpHostname,
                // sftpUsername: string or null - convert empty string to null
                sftpUsername:
                  payload.sftpUsername === '' ? null : payload.sftpUsername,
                // sftpPassword: string or null - convert empty string to null
                sftpPassword:
                  payload.sftpPassword === '' ? null : payload.sftpPassword,
                // encryptionMethod: string or null - convert empty string to null
                encryptionMethod:
                  payload.encryptionMethod === '' ||
                  payload.encryptionMethod === null
                    ? null
                    : payload.encryptionMethod,
                // publicKey: string or null - convert empty string to null
                publicKey: payload.publicKey === '' ? null : payload.publicKey,
                // copyKeysFrom: string (AnyId) - convert null/empty to undefined
                copyKeysFrom:
                  payload.copyKeysFrom === '' || payload.copyKeysFrom === null
                    ? undefined
                    : payload.copyKeysFrom,
              }
              try {
                await updateCreditAgency(cleanedPayload)
                addToast(
                  `Credit Agency ${creditAgencyId} updated successfully`,
                  'success',
                )
                await refetch()
              } catch (error: any) {
                addToast(
                  error?.message || 'Failed to update Credit Agency',
                  'error',
                )
              }
            }}
            selectOptions={{
              agencyName: [
                { label: 'Experian', value: 'experian' },
                { label: 'Equifax', value: 'equifax' },
                { label: 'TransUnion', value: 'transUnion' },
                { label: 'Innovis', value: 'innovis' },
              ],
              sftpAccessMethod: [
                { label: 'Username Password', value: 'usernamePassword' },
                { label: 'Keys', value: 'keys' },
              ],
              uploadFrequency: [{ label: 'Monthly', value: 'monthly' }],
            }}
            validationRules={{
              fileNamePrefix: [
                (v: unknown, formState: any) => {
                  const agencyName = formState.values?.agencyName
                  const nameFilePerAgencyConvention =
                    formState.values?.nameFilePerAgencyConvention
                  if (
                    agencyName === 'experian' &&
                    nameFilePerAgencyConvention === true
                  ) {
                    return (
                      (typeof v === 'string' && v.trim().length > 0) ||
                      'File name prefix is required when agency is Experian and name file per agency convention is enabled'
                    )
                  }
                  return true
                },
              ],
              sftpUsername: [
                (v: unknown, formState: any) => {
                  const sftpAccessMethod = formState.values?.sftpAccessMethod
                  if (sftpAccessMethod === 'usernamePassword') {
                    return (
                      (typeof v === 'string' &&
                        v !== null &&
                        v.trim().length > 0) ||
                      'SFTP username is required when access method is username/password'
                    )
                  }
                  return true
                },
              ],
              sftpPassword: [
                (v: unknown, formState: any) => {
                  const sftpAccessMethod = formState.values?.sftpAccessMethod
                  if (sftpAccessMethod === 'usernamePassword') {
                    return (
                      (typeof v === 'string' &&
                        v !== null &&
                        v.trim().length > 0) ||
                      'SFTP password is required when access method is username/password'
                    )
                  }
                  return true
                },
              ],
              publicKey: [
                (v: unknown, formState: any) => {
                  const encryptionMethod = formState.values?.encryptionMethod
                  if (encryptionMethod && encryptionMethod !== null) {
                    return (
                      (typeof v === 'string' &&
                        v !== null &&
                        v.trim().length > 0) ||
                      'Public key is required when encryption method is set'
                    )
                  }
                  return true
                },
              ],
              uploadFrequency: [
                (v: unknown) =>
                  v === 'monthly' || 'Upload frequency must be monthly',
              ],
              specificDays: [
                (v: unknown, formState: any) => {
                  const uploadFrequency = formState.values?.uploadFrequency
                  if (!uploadFrequency || uploadFrequency !== 'monthly') {
                    return true
                  }
                  if (!Array.isArray(v)) {
                    return 'Specific days must be an array'
                  }
                  // Allow empty array - it's optional
                  if (v.length === 0) {
                    return true
                  }
                  if (v.length > 1) {
                    return 'Only one specific day is supported for monthly upload frequency'
                  }
                  const day = v[0]
                  if (typeof day !== 'number' || !Number.isInteger(day)) {
                    return 'Specific days must be integers'
                  }
                  if (day < 1 || day > 31) {
                    return 'Specific day must be between 1 and 31 (1=first day, 31=last day)'
                  }
                  return true
                },
              ],
            }}
          />
        </Page>
      </LoadingContainer>
    </Panels>
  )
}

export default CreditAgency
